import React, { useEffect, useState } from 'react';
import { teaching, leadership, testimonials } from '../data/mockData';
import { COLORS, SPACING } from '../constants/designSystem';
import { GraduationCap, Users, BookOpen, Award } from 'lucide-react';

const TeachingPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const typeIcons = {
    Bootcamp: BookOpen,
    Mentorship: Users,
    University: GraduationCap,
    Workshop: Users,
    Community: Users,
    'Open Source': Award,
    Professional: Award,
  };

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh', paddingTop: '80px' }}>
      {/* Hero */}
      <section
        style={{
          padding: `${SPACING.xxxl} ${SPACING.lg}`,
          textAlign: 'center',
          background: `radial-gradient(circle at 50% 50%, ${COLORS.primary}08 0%, transparent 60%)`,
        }}
      >
        <div
          style={{
            maxWidth: '800px',
            margin: '0 auto',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <div
            style={{
              display: 'inline-block',
              padding: `${SPACING.xs} ${SPACING.md}`,
              backgroundColor: `${COLORS.primary}15`,
              border: `1px solid ${COLORS.primary}40`,
              borderRadius: '50px',
              marginBottom: SPACING.md,
            }}
          >
            <span style={{ fontSize: '14px', color: COLORS.primary, fontWeight: '500' }}>Empowering Others</span>
          </div>
          <h1
            style={{
              fontSize: '56px',
              fontWeight: '700',
              color: COLORS.text.primary,
              marginBottom: SPACING.md,
            }}
          >
            Teaching & Mentorship
          </h1>
          <p
            style={{
              fontSize: '18px',
              lineHeight: '1.6',
              color: COLORS.text.secondary,
            }}
          >
            Helping aspiring developers find their path in tech through education, mentorship, and community leadership
          </p>
        </div>
      </section>

      {/* Teaching Experience */}
      <section style={{ padding: `${SPACING.xl} ${SPACING.lg}` }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2
            style={{
              fontSize: '36px',
              fontWeight: '700',
              color: COLORS.text.primary,
              marginBottom: SPACING.xl,
              textAlign: 'center',
            }}
          >
            Teaching Experience
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: SPACING.lg,
            }}
          >
            {teaching.map((item, index) => {
              const Icon = typeIcons[item.type] || GraduationCap;
              return (
                <div
                  key={item.id}
                  style={{
                    padding: SPACING.lg,
                    backgroundColor: COLORS.cardSurface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '16px',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: `all 500ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 80}ms`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = COLORS.primary;
                    e.currentTarget.style.transform = 'translateY(-8px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = COLORS.border;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      backgroundColor: `${COLORS.primary}15`,
                      border: `2px solid ${COLORS.primary}40`,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: SPACING.md,
                    }}
                  >
                    <Icon size={28} color={COLORS.primary} strokeWidth={2} />
                  </div>
                  <h3
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: COLORS.text.primary,
                      marginBottom: SPACING.xs,
                    }}
                  >
                    {item.title}
                  </h3>
                  <p
                    style={{
                      fontSize: '14px',
                      color: COLORS.primary,
                      fontWeight: '500',
                      marginBottom: SPACING.xs,
                    }}
                  >
                    {item.organization}
                  </p>
                  <p
                    style={{
                      fontSize: '14px',
                      color: COLORS.text.muted,
                      marginBottom: SPACING.md,
                    }}
                  >
                    {item.period}
                  </p>
                  <p
                    style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: COLORS.text.secondary,
                      marginBottom: SPACING.md,
                    }}
                  >
                    {item.description}
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING.sm,
                      padding: `${SPACING.sm} 0`,
                      borderTop: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <Users size={16} color={COLORS.primary} />
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: COLORS.primary,
                      }}
                    >
                      {item.students} Students Impacted
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section style={{ padding: `${SPACING.xl} ${SPACING.lg}`, backgroundColor: COLORS.surface }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2
            style={{
              fontSize: '36px',
              fontWeight: '700',
              color: COLORS.text.primary,
              marginBottom: SPACING.xl,
              textAlign: 'center',
            }}
          >
            Leadership Roles
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: SPACING.md,
            }}
          >
            {leadership.map((item, index) => {
              const Icon = typeIcons[item.type] || Award;
              return (
                <div
                  key={item.id}
                  style={{
                    padding: SPACING.md,
                    backgroundColor: COLORS.cardSurface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '12px',
                    opacity: isVisible ? 1 : 0,
                    transition: `opacity 500ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 60}ms`,
                  }}
                >
                  <div style={{ display: 'flex', gap: SPACING.sm, marginBottom: SPACING.sm }}>
                    <Icon size={20} color={COLORS.primary} />
                    <div style={{ flex: 1 }}>
                      <h4
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: COLORS.text.primary,
                          marginBottom: '4px',
                        }}
                      >
                        {item.title}
                      </h4>
                      <p
                        style={{
                          fontSize: '13px',
                          color: COLORS.primary,
                          marginBottom: '4px',
                        }}
                      >
                        {item.organization}
                      </p>
                      <p
                        style={{
                          fontSize: '12px',
                          color: COLORS.text.muted,
                        }}
                      >
                        {item.period}
                      </p>
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: COLORS.text.secondary,
                    }}
                  >
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: `${SPACING.xxxl} ${SPACING.lg}` }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2
            style={{
              fontSize: '36px',
              fontWeight: '700',
              color: COLORS.text.primary,
              marginBottom: SPACING.xl,
              textAlign: 'center',
            }}
          >
            Student Testimonials
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: SPACING.lg,
            }}
          >
            {testimonials.map((item, index) => (
              <div
                key={item.id}
                style={{
                  padding: SPACING.lg,
                  backgroundColor: COLORS.cardSurface,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '16px',
                  opacity: isVisible ? 1 : 0,
                  transition: `opacity 500ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 100}ms`,
                }}
              >
                <p
                  style={{
                    fontSize: '15px',
                    lineHeight: '1.7',
                    color: COLORS.text.secondary,
                    fontStyle: 'italic',
                    marginBottom: SPACING.md,
                  }}
                >
                  "{item.text}"
                </p>
                <div style={{ display: 'flex', gap: SPACING.sm, alignItems: 'center' }}>
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: `2px solid ${COLORS.primary}`,
                    }}
                  />
                  <div>
                    <p
                      style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: COLORS.text.primary,
                        marginBottom: '2px',
                      }}
                    >
                      {item.name}
                    </p>
                    <p style={{ fontSize: '13px', color: COLORS.text.muted }}>{item.role}</p>
                    <p style={{ fontSize: '12px', color: COLORS.primary }}>{item.organization}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeachingPage;
